import axios from 'axios';
import { getApiUrl } from '../../../config/api';

/**
 * API client for company-side recruiter management (Settings → Recruiters).
 * Mirrors savedInterviewersApi.js: the backend reads the company identity from
 * the session, and we also pass it as company_email so the call works when
 * session cookies aren't available (staging vs. localhost cross-origin).
 */

const ACTOR_KEYS = [
  'admin_email',
  'employer_email',
  'recruiter_email',
  'company_email',
  'organizer_email',
];

const readStored = (key) => {
  if (typeof window === 'undefined') return '';
  return window.sessionStorage.getItem(key) || window.localStorage.getItem(key) || '';
};

const getCompanyEmail = () => {
  if (typeof window === 'undefined') return '';
  for (const key of ACTOR_KEYS) {
    const value = readStored(key);
    if (value) return value.trim().toLowerCase();
  }
  return '';
};

/** List the company's recruiters (roster + access) and the company's jobs. */
export async function listCompanyRecruiters() {
  const params = {};
  const company = getCompanyEmail();
  if (company) params.company_email = company;
  const res = await axios.get(getApiUrl('/api/company/recruiters'), {
    params,
    withCredentials: true,
  });
  return {
    recruiters: Array.isArray(res.data?.recruiters) ? res.data.recruiters : [],
    jobs: Array.isArray(res.data?.jobs) ? res.data.jobs : [],
  };
}

/** Add a recruiter to the roster (creates the account + emails an invite if new),
 *  optionally granting access to the given job ids. */
export async function addCompanyRecruiter({ recruiterEmail, jobIds = [] } = {}) {
  const normalized = String(recruiterEmail || '').trim().toLowerCase();
  if (!normalized) throw new Error('Recruiter email is required.');
  const company = getCompanyEmail();
  const res = await axios.post(
    getApiUrl('/api/company/recruiters'),
    {
      company_email: company || undefined,
      recruiter_email: normalized,
      job_ids: jobIds,
    },
    { withCredentials: true }
  );
  return res.data;
}

/** Set a recruiter's JD access EXACTLY to jobIds (grants/revokes the diff). */
export async function setRecruiterAccess({ recruiterEmail, jobIds = [] } = {}) {
  const normalized = String(recruiterEmail || '').trim().toLowerCase();
  if (!normalized) throw new Error('Recruiter email is required.');
  const company = getCompanyEmail();
  const res = await axios.patch(
    getApiUrl('/api/company/recruiters/access'),
    {
      company_email: company || undefined,
      recruiter_email: normalized,
      job_ids: jobIds,
    },
    { withCredentials: true }
  );
  return res.data;
}

/** Remove a recruiter from the company (revokes access to your jobs + roster). */
export async function removeCompanyRecruiter({ recruiterEmail } = {}) {
  const normalized = String(recruiterEmail || '').trim().toLowerCase();
  if (!normalized) throw new Error('Recruiter email is required.');
  const company = getCompanyEmail();
  const res = await axios.delete(getApiUrl('/api/company/recruiters'), {
    data: { company_email: company || undefined, recruiter_email: normalized },
    params: company ? { company_email: company } : undefined,
    withCredentials: true,
  });
  return Boolean(res.data?.removed);
}
